/**
 * Chat send hook — orchestrates handleSendMessage.
 * Delegates payload building to chatApi, streaming to streamingHandler,
 * non-streaming to nonStreamingHandler.
 */
import { useState, useCallback } from 'react';
import { devLog, devError } from '../utils/devLog';
import { updateUserStatsFromHeaders } from '../utils/chatUtils';
import { buildPayload, convertImagesToBase64, callApi, detectToolHint, pollForResponse } from '../utils/chatApi';
import { processStream } from '../utils/streamingHandler';
import { processNonStreamingResponse } from '../utils/nonStreamingHandler';
import memoryApi from '../api/memoryApi';
import {
  ALL_AI_MODELS,
  IS_POPUP_ENABLED as isPopupEnabled,
  ENABLE_MIMICK as enable_mimick,
} from '../config/chatConfig';

export default function useChatSend({
  messages, setMessages, setMessagesForSession,
  isLoading, setIsLoading, setIsLoadingForSession,
  user, setUser, selectedModel, setSelectedModel,
  uploadImages, selectedImages, setSelectedImages, setUploadImages,
  currentSessionId, setCurrentSessionId, setSessions, setIsCreatingSession,
  navigate, refreshSessions, setQnaData,
  abortControllerRef, abortControllersBySessionRef,
  intervalIdRef, mockTimeoutPolling, screenshotInProgress,
  memoryManagerRef, handleScreenshotRequest, handleCreateSession,
  setIsPopupOpen, setIsMimicking, scrollToBottom,
  bumpSessionToTop, addSessionToTop, patchSession,
}) {
  const [isWebSearching, setIsWebSearching] = useState(false);
  const [currentTool, setCurrentTool] = useState(null);
  const [stopRequested, setStopRequested] = useState(false);

  const handleSendMessage = useCallback(async (input) => {
    if (!input.trim() && (uploadImages.length === 0 || isLoading)) return;

    if (!user && isPopupEnabled) {
      setIsPopupOpen(true);
      return;
    }

    // Create session on first message if none exists
    let activeSessionId = currentSessionId;
    if (!activeSessionId) {
      try {
        setIsCreatingSession(true);
        const newSession = await memoryApi.createSession('New Chat');
        setSessions(prev => [newSession, ...prev]);
        // Mirror into App.js's chatSessions (the state ChatSidebar renders
        // from). Without this the new session is invisible in the sidebar
        // until the next list refetch — which we no longer trigger on path
        // change. Optimistic insert keeps the sidebar instantly accurate.
        addSessionToTop?.(newSession);
        activeSessionId = newSession.session_id;

        sessionStorage.setItem('pendingFirstMessage', JSON.stringify({
          text: input,
          images: uploadImages.length > 0 && selectedImages.length > 0 ? selectedImages : []
        }));

        navigate(`/chatty/${newSession.session_id}`, { replace: true });
        devLog(`📝 Created new session, navigating with pending message: ${newSession.session_id}`);
        return;
      } catch (error) {
        devError("Failed to create session:", error);
        setIsCreatingSession(false);
        return;
      }
    }

    // Build user message
    const userMessage = {
      role: "user",
      text: input,
      ...(uploadImages.length > 0 && selectedImages.length > 0 ? { images: selectedImages } : {})
    };

    // ── Session-bound writers (closure over activeSessionId) ────────────
    // Streams from this send write to THIS session's bucket, not the
    // currently-displayed session. User can switch away and back; the
    // stream keeps writing to the original session. Long-running tool
    // calls in session A no longer block the UI when user is in B.
    const setStreamMessages = (updater) =>
      setMessagesForSession?.(activeSessionId, updater);
    const setStreamLoading = (value) =>
      setIsLoadingForSession?.(activeSessionId, value);

    setStreamMessages((prev) => [...prev, userMessage]);
    const requestSessionId = activeSessionId;

    // Bump sidebar order at SEND time, not at response time. Under rapid
    // session-switching the response arrival order is non-deterministic
    // (network jitter, variable server time) — bumping on response would
    // re-order older sessions on top of newer ones if the older one's
    // response happened to arrive later. Send-time bump is monotonic
    // with user intent: the session you're currently sending in moves
    // to position 0 instantly, regardless of when the AI replies.
    // Returns a revert handle: invoked on fetch error / abort so the
    // sidebar settles to real backend state when the send didn't persist.
    const revertBump = bumpSessionToTop?.(activeSessionId) || (() => {});

    if (memoryManagerRef.current) {
      memoryManagerRef.current.addUserMessage(userMessage.text);
    }

    // Clear images
    setSelectedImages([]);
    setUploadImages([]);
    localStorage.removeItem('chatty_selectedImages');
    localStorage.removeItem('chatty_uploadImages');
    const fileInput = document.getElementById("image-upload");
    if (fileInput) fileInput.value = "";

    setStreamLoading(true);
    setIsWebSearching(false);
    setStopRequested(false);

    const modelConfig = ALL_AI_MODELS[selectedModel];
    const useStream = modelConfig.stream;

    devLog(`🤖 Selected Model: ${selectedModel}`);
    devLog(`📡 Stream Mode: ${useStream}`);

    // Detect tool hint for loading animation
    setCurrentTool(detectToolHint(input));

    // Per-session abort controller. If THIS session has another in-flight
    // request, abort it (rare — would mean user spammed send in same
    // session). Other sessions' controllers are NOT touched: their
    // streams keep going regardless of this new send.
    const sessionMap = abortControllersBySessionRef?.current;
    const existingForSession = sessionMap?.get(activeSessionId);
    if (existingForSession) {
      existingForSession.abort();
    }
    const newController = new AbortController();
    if (sessionMap) sessionMap.set(activeSessionId, newController);
    if (activeSessionId === currentSessionId) {
      abortControllerRef.current = newController;
    }

    const timeoutId = setTimeout(() => {
      const ctrl = sessionMap?.get(activeSessionId);
      if (ctrl === newController) {
        devLog(`⏰ Request timeout (10 min) — aborting session=${activeSessionId}`);
        ctrl.abort();
      }
    }, 600000);

    try {
      // Convert images to base64
      let imagesData = null;
      if (uploadImages.length > 0) {
        if (!modelConfig.supportsImages) {
          alert("This model doesn't support image uploads.");
          setStreamLoading(false);
          return;
        }
        imagesData = await convertImagesToBase64(uploadImages);
      }

      // Build payload and call API
      const payload = buildPayload(input, selectedModel, activeSessionId, imagesData);

      let response;
      try {
        response = await callApi(payload, selectedModel, newController.signal, user);
      } catch (extError) {
        // Extended timeout failed — start polling
        devLog(`⚠️ Extended timeout error: ${extError.message} - Backend still processing`);
        setStreamLoading(true);
        mockTimeoutPolling.current = true;
        pollForResponse(activeSessionId, messages.length, { setMessages: setStreamMessages, setIsLoading: setStreamLoading, mockTimeoutPolling });
        return;
      }

      devLog(`📡 Response status: ${response.status}`);

      // Update user stats from headers (non-DeepSeek, non-Kaidex)
      if (selectedModel !== "deepseek-r1:32b" && selectedModel !== "kaidex-local") {
        updateUserStatsFromHeaders(response, setUser);
      }

      // Handle error responses
      if (!response.ok) {
        const errorData = await response.json();
        devLog(" API Error Response:", errorData);

        if (response.status === 400 && errorData.error === "SESSION_LIMIT_REACHED") {
          setStreamLoading(false);
          const shouldCreateNew = window.confirm(
            `⚠️ ${errorData.message || "This conversation has reached the maximum length."}\n\nWould you like to create a new session?`
          );
          if (shouldCreateNew) await handleCreateSession();
          return;
        }

        if (response.status === 403) {
          setStreamLoading(false);
          alert(errorData.message || "Message limit reached.");
          if (errorData.messagesUsed !== undefined) {
            setUser((prev) => ({
              ...prev,
              'custom:messagesUsed': errorData.messagesUsed.toString(),
              'custom:limit': errorData.limit.toString(),
              'custom:resetDate': errorData.resetDate,
            }));
          }
          return;
        }
        throw new Error(`HTTP Error: ${response.status} ${errorData.message || response.statusText}`);
      }

      // Routing decision: trust the response's Content-Type, not the pre-request guess.
      // ai_stream returns 'text/event-stream'. ai_manager_v2 returns 'application/json'.
      // This avoids the branch/payload mismatch when admin toggles the StreamModeToggle off
      // mid-session (payload said stream:false, so ai_manager_v2 returns JSON).
      const contentType = (response.headers.get('content-type') || '').toLowerCase();
      const isSSE = contentType.includes('text/event-stream');

      // ==================== STREAMING ====================
      if (selectedModel !== "deepseek-r1:32b" && isSSE) {
        updateUserStatsFromHeaders(response, setUser);
        // setMessages bound to activeSessionId — chunks land in the
        // session that issued the send, not whatever session is active
        // when the chunk arrives.
        // Title-update path: sidebar reads from App.js's chatSessions
        // (mutated via patchSession from ChatInputContext), breadcrumb reads
        // from useChatSessions.sessions (mutated via setSessions). Pass both
        // so the localized `sessionTitle` SSE frame updates both surfaces
        // without a full list refetch.
        await processStream(response, {
          setMessages: setStreamMessages, setUser, setIsWebSearching, isWebSearching, setQnaData,
          setSessions, patchSession, currentSessionId: activeSessionId,
        });
      }
      // ==================== NON-STREAMING ====================
      else {
        const data = await response.json();
        const { handled } = processNonStreamingResponse(data, selectedModel, {
          setMessages: setStreamMessages, setIsWebSearching, setQnaData, setIsMimicking,
          enable_mimick, stopRequested, intervalIdRef, memoryManagerRef,
          screenshotInProgress, handleScreenshotRequest,
          userMessageText: userMessage.text || input,
          currentSessionId: activeSessionId, setSessions, refreshSessions,
        });
        if (handled) return;
      }

      // (Sidebar reorder already done at SEND time — no post-response
      // bump here. See above where bumpSessionToTop fires the moment
      // the user dispatches the message.)

    } catch (error) {
      // Send didn't persist — undo the optimistic sidebar bump so the
      // session's position reflects real backend state, not a failed
      // attempt. Backend's save runs at end-of-stream / on success only,
      // so any error here means nothing was saved server-side.
      revertBump();

      if (error.name === "AbortError") {
        if (!stopRequested) {
          devError("❌ Request aborted unexpectedly");
          setStreamMessages((prev) => [...prev, { role: "ai", text: "⚠️ Connection interrupted. Please try again." }]);
        }
        setStreamLoading(false);
      } else {
        console.error("❌ Error fetching AI response:", error);
        let errorMessage = "⚠️ AI response error. Try again later.";
        if (process.env.NODE_ENV === 'development') {
          errorMessage += ` (${error.name}: ${error.message})`;
        }
        setStreamMessages((prev) => [...prev, { role: "ai", text: errorMessage }]);
      }
    } finally {
      clearTimeout(timeoutId);

      if (!mockTimeoutPolling.current && !screenshotInProgress.current) {
        setStreamLoading(false);
      }
      setIsWebSearching(false);
      // Per-session abort cleanup — only clear THIS session's controller,
      // and only if it's still the same one (in case of race with a
      // newer send for same session). Other sessions' controllers stay
      // intact so their streams keep going.
      const sm = abortControllersBySessionRef?.current;
      if (sm && sm.get(activeSessionId) === newController) {
        sm.delete(activeSessionId);
      }
      if (abortControllerRef.current === newController) {
        abortControllerRef.current = null;
      }

      // No reload-on-session-switch needed anymore: the per-session
      // bucket holds this stream's output, and switching just re-reads
      // the active session's bucket. Old code reloaded the new session's
      // messages because the single-array state was being polluted.
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadImages, selectedImages, user, isLoading, setMessages, setMessagesForSession, selectedModel, setSelectedImages, setUploadImages, setIsPopupOpen, setUser, stopRequested, isWebSearching, setIsLoading, setIsLoadingForSession, scrollToBottom, messages, currentSessionId, refreshSessions, bumpSessionToTop, addSessionToTop]);

  return {
    handleSendMessage,
    handleStopMessage: useCallback(() => {
      // Stop only the currently-active session's stream. Other sessions'
      // streams keep running in the background. abortControllerRef tracks
      // the active session's controller (synced via Chatty's effect on
      // currentSessionId).
      setStopRequested(true);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        // Remove from the per-session map too so a fresh send isn't
        // matched against an aborted controller.
        const sm = abortControllersBySessionRef?.current;
        if (sm && currentSessionId) sm.delete(currentSessionId);
        abortControllerRef.current = null;
      }
      setIsLoading(false);
      setIsMimicking(false);
      if (intervalIdRef.current) { clearInterval(intervalIdRef.current); intervalIdRef.current = null; }
    }, [setIsLoading, setIsMimicking, abortControllerRef, abortControllersBySessionRef, currentSessionId, intervalIdRef]),
    isWebSearching,
    currentTool,
    stopRequested, setStopRequested,
  };
}
